import { Inject, Injectable } from '@angular/core';
import {
  ConnectionHttpService,
  ApiResourceLocation,
  SOFTLINE_SERVICE_HTTP,
  Result,
} from '@softline/core';
import { Emballage } from '../types/warenuebernahme';
import {
  Bewe,
  Bewelg,
  PositionErledigenSplit,
  WarenuebernahmeBeweEtikett,
} from '../types/bewegung';
import {
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN,
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN_LAGERZEILE,
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN_MISCHPALETTE,
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_KONTROLL,
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_OEFFNEN,
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_OEFFNEN_LAGERZEILE,
  SOFTLINE_API_WWS_WARENUEBERNAHME_EMBALLAGEN_HINZUFUEGEN,
  SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN_MISCHPALETTE_LADEZEILE,
  SOFTLINE_API_WWS_WARENUEBERNAHME_PALETTE_TEMPLATE,
} from '../warenuebernahme.api';
import { map, Observable } from 'rxjs';
import { PaletteBean } from '../types/pallet-item';

@Injectable()
export class BewegungenService {
  private positionKontrolllocation(id: number): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_KONTROLL,
      pathParams: {
        id,
      },
    };
  }

  private positionErledigenlocation(): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN,
    };
  }

  private positionErledigenMischPalettelocation(): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN_MISCHPALETTE,
    };
  }

  private positionErledigenMischPaletteLademittelLocation(): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN_MISCHPALETTE_LADEZEILE,
    };
  }

  private positionErledigenLademittelLocation(): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_ERLEDIGEN_LAGERZEILE,
    };
  }

  private paletteTemplateLocation(idbewe: string | number): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_PALETTE_TEMPLATE,
      pathParams: { idbewe },
    };
  }

  private positionOeffnenlocation(): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_OEFFNEN,
    };
  }

  private positionOeffnenLademittelLocation(): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_BEWES_POSITION_OEFFNEN_LAGERZEILE,
    };
  }

  private emballageHinzufuegenLocation(idwu: string): ApiResourceLocation {
    return {
      path: SOFTLINE_API_WWS_WARENUEBERNAHME_EMBALLAGEN_HINZUFUEGEN,
      pathParams: { idwu },
    };
  }

  constructor(
    @Inject(SOFTLINE_SERVICE_HTTP) private httpService: ConnectionHttpService
  ) {}

  positionKonroll(id: number): Observable<{ geloescht: 'kopf' | 'bewegung' }> {
    const location = this.positionKontrolllocation(id);

    return this.httpService.delete(location);
  }

  positionErledigen(payload: WarenuebernahmeBeweEtikett): Observable<Bewe> {
    const location = this.positionErledigenlocation();

    return this.httpService.create<WarenuebernahmeBeweEtikett, Bewe>(location, payload);
  }

  positionErledigenMischPalette(payload: any): Observable<any> {
    const location = this.positionErledigenMischPalettelocation();

    return this.httpService.create<any, any>(location, payload);
  }

  paletteTemplate(idbewe: string | number): Observable<PaletteBean | null> {
    return this.httpService
      .get<Result<PaletteBean | null>>(this.paletteTemplateLocation(idbewe))
      .pipe(map((r) => r?.result ?? null));
  }

  positionOeffnen(payload: Bewe): Observable<Bewe> {
    const location = this.positionOeffnenlocation();

    return this.httpService.create<Bewe, Bewe>(location, payload);
  }

  positionErledigenMischPaletteLademittel(payload: {
    palette: any;
    bewelg: Bewelg;
  }): Observable<{
    palette: any;
    bewelg: Bewelg;
  }> {
    const location = this.positionErledigenMischPaletteLademittelLocation();
    return this.httpService.create<
      {
        palette: any;
        bewelg: Bewelg;
      },
      {
        palette: any;
        bewelg: Bewelg;
      }
    >(location, payload);
  }

  positionErledigenLademittel(
    payload: { anzahlEtiketten: number; bewelgBean: Bewelg } & PositionErledigenSplit
  ): Observable<Bewelg> {
    const location = this.positionErledigenLademittelLocation();
    return this.httpService.create<any, Bewelg>(location, payload);
  }

  positionOeffnenLademittel(payload: Bewelg): Observable<Bewelg> {
    const location = this.positionOeffnenLademittelLocation();
    return this.httpService.create<Bewelg, Bewelg>(location, payload);
  }

  emballageHinzufuegen(payload: Emballage, idwu: string): Observable<Bewe> {
    const location = this.emballageHinzufuegenLocation(idwu);

    return this.httpService.create<Emballage, Bewe>(location, payload);
  }
}
